import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Loader2, MailCheck } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, formatApiError } from '@/lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-32 md:pt-40 pb-24 container mx-auto">
        <div className="max-w-md mx-auto rounded-3xl bg-card border border-border/70 shadow-card p-8 sm:p-10">
          <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
            <KeyRound className="h-5 w-5" />
          </div>
          {sent ? (
            <div data-testid="forgot-success">
              <h1 className="font-display text-3xl font-semibold text-primary mt-5 flex items-center gap-2">
                Check your inbox <MailCheck className="h-6 w-6 text-leaf" />
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                If an account exists for <strong className="text-primary">{email}</strong>, a password
                reset link is on its way. The link expires in 1 hour.
              </p>
              <Button asChild variant="outline" className="w-full rounded-full mt-6">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-primary mt-5">Forgot password</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                Enter your account email and we'll send you a reset link.
              </p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="forgot-form">
                <div>
                  <Label htmlFor="fp-email" className="text-primary mb-1.5">Email</Label>
                  <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com" className="h-11 rounded-xl" data-testid="forgot-email-input" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="forgot-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-6 text-center">
                Remembered it? <Link to="/login" className="text-leaf hover:underline underline-offset-4">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
